import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

type Recipe = {
  id: string;
  name?: string;
  sport?: string;
  description?: string;
  active?: boolean;
  templateId?: string;
  slots?: unknown[];
  createdAt?: string;
  updatedAt?: string;
};
type Payload = { recipes?: Recipe[] } | Recipe[];

export default function Recipes() {
  const { data, isLoading, error } = useSWR<Payload>('/api/recipes', get);

  // sport-templates may return { recipes: [...] } or just [...]
  const recipes: Recipe[] = Array.isArray(data) ? data : (data?.recipes || []);

  return (
    <div className="px-7 py-6">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Recipes</h1>
          <p className="text-[12px] text-muted">Submission recipes from sport-templates · {recipes.length} loaded · read-only view</p>
        </div>
        <Link
          to="/template-author"
          className="shrink-0 rounded-[8px] border border-black/[0.08] bg-surface px-3 py-1.5 text-[11.5px] font-medium text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]"
        >
          New Design →
        </Link>
      </div>

      {error ? (
        <div className="rounded-md bg-danger/10 p-4 text-[12px] text-danger">
          Couldn't load recipes from sport-templates. {(error as Error)?.message || ''}
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : recipes.length ? (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {recipes.map((r) => (
            <div key={r.id} className="rounded-md bg-surface p-3 shadow-card">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-subtle truncate">{r.id}</span>
                {r.active === false && <Badge className="bg-bg text-muted">inactive</Badge>}
                {r.sport && <Badge className="bg-success/10 text-success">{r.sport}</Badge>}
              </div>
              <h3 className="mt-1 text-[12.5px] font-semibold truncate">{r.name || 'Untitled'}</h3>
              {r.description && <p className="mt-1 line-clamp-2 text-[11px] text-muted">{r.description}</p>}
              {r.slots && Array.isArray(r.slots) && (
                <div className="mt-2 text-[10.5px] text-subtle">{r.slots.length} slots</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-surface p-8 text-center text-[12px] text-muted shadow-card">
          No recipes recorded yet.
        </div>
      )}
    </div>
  );
}
